import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SignaturePad from '../claims/SignaturePad';
import {
  AmendableSection,
  StagedSections,
} from '../../@types/clinicalRecordAmendment';
import { sectionLabel } from './amendmentLabels';

type Props = {
  open: boolean;
  loading: boolean;
  stagedSections: AmendableSection[];
  staged: StagedSections;
  reasonPresets: string[];
  nextVersion: number;
  onClose: () => void;
  onSubmit: (input: {
    reason: string;
    reasonPreset?: string;
    signatureUrl: string;
  }) => void;
};

const MIN_REASON = 5;

export default function AmendmentSignDialog({
  open,
  loading,
  stagedSections,
  staged,
  reasonPresets,
  nextVersion,
  onClose,
  onSubmit,
}: Props) {
  const [preset, setPreset] = useState('');
  const [reason, setReason] = useState('');
  const [signature, setSignature] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setTouched(false);
      setSignature('');
    }
  }, [open]);

  const trimmed = reason.trim();
  const reasonError = touched && trimmed.length < MIN_REASON;
  const signatureError = touched && !signature;
  const nothingStaged = stagedSections.length === 0;

  const handlePreset = (value: string) => {
    setPreset(value);
    // Pre-fill the free text from the preset, without overwriting what the user typed.
    if (
      value &&
      value !== 'Other' &&
      (!trimmed || reasonPresets.includes(trimmed))
    ) {
      setReason(value);
    }
  };

  const handleSubmit = () => {
    setTouched(true);
    if (trimmed.length < MIN_REASON || !signature || nothingStaged) return;
    onSubmit({
      reason: trimmed,
      reasonPreset: preset || undefined,
      signatureUrl: signature,
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={loading ? undefined : onClose}
    >
      <DialogTitle>Sign amendment (version {nextVersion})</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <div>
            <Typography variant="subtitle2" gutterBottom>
              Sections changed
            </Typography>
            {nothingStaged ? (
              <Alert severity="info">
                No changes have been added yet. Edit a section and click its
                Save button to add it to this amendment.
              </Alert>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {stagedSections.map((section) => (
                  <Chip
                    key={section}
                    size="small"
                    color="warning"
                    variant="soft"
                    label={
                      section === 'trails'
                        ? `${sectionLabel(section)} (+${staged.trails?.length ?? 0})`
                        : sectionLabel(section)
                    }
                  />
                ))}
              </Stack>
            )}
          </div>

          <TextField
            select
            label="Reason preset (optional)"
            value={preset}
            onChange={(e) => handlePreset(e.target.value)}
            disabled={loading}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {reasonPresets.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            required
            multiline
            minRows={3}
            label="Reason for amendment"
            placeholder="e.g. Right eye sphere was transcribed as +1.25, the measured value was -1.25."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            error={reasonError}
            helperText={
              reasonError
                ? `A reason is required (at least ${MIN_REASON} characters).`
                : 'Shown on the record and stored in the audit log with every changed field.'
            }
            inputProps={{ maxLength: 2000 }}
            disabled={loading}
          />

          <SignaturePad
            label="Your signature"
            value={signature}
            onChange={setSignature}
            error={
              signatureError
                ? 'You must sign to save the amendment.'
                : undefined
            }
            disabled={loading}
          />

          <Typography variant="caption" color="text.secondary">
            Signing saves version {nextVersion} with your name and the current
            time. The original signed version is kept unchanged. If you close
            this dialog without signing, nothing is saved.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={loading}
        >
          Back to editing
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={nothingStaged}
          onClick={handleSubmit}
        >
          Sign &amp; save amendment
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
